/**
 * Usenlease Centralized Theme Configuration
 *
 * Brand anchors:
 *   #ff9c00 / #fe9d00 — main brand amber
 *   #ffffff            — white
 *   #555555            — mid gray
 *   #1c1c1c            — near-black
 *
 * Palette kept warm & muted — never neon, never shouty.
 */

export const colors = {
  // Primary — brand amber (buttons, CTAs, hero, accents)
  primary: {
    50:  '#fef9f0',
    100: '#fdf0d6',
    200: '#fbe0ad',
    300: '#f8cc80',
    400: '#f5b54e',
    500: '#ff9c00',   // #ff9c00 brand
    600: '#e68d00',   // main action shade — slightly deeper for contrast
    700: '#c47800',
    800: '#9c6000',
    900: '#7a4b00',
  },
  // Secondary — softer warm gold (subtle highlights, badges, secondary accents)
  secondary: {
    50:  '#fefaf3',
    100: '#fdf2de',
    200: '#fbe5c0',
    300: '#f8d49a',
    400: '#f4c06e',
    500: '#f0ad45',   // muted golden
    600: '#d99a3a',
    700: '#b88030',
    800: '#946626',
    900: '#704d1d',
  },
  // Neutral — gray/dark scale anchored to brand values
  neutral: {
    50:  '#fafaf9',
    100: '#f5f4f2',
    200: '#edeceb',
    300: '#d6d5d3',
    400: '#b0aeab',
    500: '#888784',
    600: '#555555',   // #555555 brand gray
    700: '#3d3d3d',
    800: '#2a2a2a',
    900: '#1c1c1c',   // #1C1C1C brand dark
  },
}

export const themes = {
  light: {
    name: 'light',
    // Backgrounds — clean white, warm off-whites
    bg:           '#ffffff',
    bgSecondary:  '#fafaf8',
    bgTertiary:   '#f5f3f0',
    surface:      '#ffffff',
    surfaceHover: '#fdfcfa',
    // Text — always dark in light mode
    text:         '#1c1c1c',
    textSecondary:'#555555',
    textMuted:    '#7a7a7a',
    // Borders — warm, subtle
    border:       '#e5e3de',
    borderLight:  '#eeedea',
    // Navigation
    navBg:        '#ffffff',
    navText:      '#000000',
    // Footer
    footerBg:     '#f5f3f0',
    footerText:   '#1c1c1c',
    // Cards + inputs
    cardBg:       '#ffffff',
    inputBg:      '#ffffff',
    inputBorder:  '#dddbd5',
    inputText:    '#1c1c1c',
    // Overlay
    overlay:      'rgba(28,28,28,0.45)',
    // Sidebar
    sidebarBg:    '#fafaf8',
    sidebarText:  '#1c1c1c',
  },

  // ── Dark themes ────────────────────────────────────────────────────────

  // VS Code Dark — the default dark theme
  dark: {
    name: 'VS Code Dark',
    bg:           '#1e1e1e',
    bgSecondary:  '#252526',
    bgTertiary:   '#2d2d2d',
    surface:      '#252526',
    surfaceHover: '#2a2d2e',
    text:         '#cccccc',
    textSecondary:'#999999',
    textMuted:    '#666666',
    border:       '#3a3a3a',
    borderLight:  '#323232',
    navBg:        '#181818',
    navText:      '#f0f0f0',
    footerBg:     '#181818',
    footerText:   '#999999',
    cardBg:       '#252526',
    inputBg:      '#333333',
    inputBorder:  '#444444',
    inputText:    '#cccccc',
    overlay:      'rgba(0,0,0,0.65)',
    sidebarBg:    '#1e1e1e',
    sidebarText:  '#cccccc',
  },

  // GitHub Dimmed — muted blue-gray, very easy on eyes
  'dark-dimmed': {
    name: 'GitHub Dimmed',
    bg:           '#22272e',
    bgSecondary:  '#2d333b',
    bgTertiary:   '#373e47',
    surface:      '#2d333b',
    surfaceHover: '#373e47',
    text:         '#adbac7',
    textSecondary:'#8b949e',
    textMuted:    '#636e7b',
    border:       '#444c56',
    borderLight:  '#373e47',
    navBg:        '#1c2128',
    navText:      '#e0e6ec',
    footerBg:     '#1c2128',
    footerText:   '#8b949e',
    cardBg:       '#2d333b',
    inputBg:      '#373e47',
    inputBorder:  '#444c56',
    inputText:    '#adbac7',
    overlay:      'rgba(28,33,40,0.7)',
    sidebarBg:    '#22272e',
    sidebarText:  '#adbac7',
  },

  // Oceanic — deep sea teal, relaxing
  'dark-oceanic': {
    name: 'Oceanic',
    bg:           '#1b2b34',
    bgSecondary:  '#213440',
    bgTertiary:   '#29404d',
    surface:      '#213440',
    surfaceHover: '#29404d',
    text:         '#bccdd9',
    textSecondary:'#8aa3b4',
    textMuted:    '#5c7888',
    border:       '#345060',
    borderLight:  '#2a3f4d',
    navBg:        '#152229',
    navText:      '#dce8f0',
    footerBg:     '#152229',
    footerText:   '#8aa3b4',
    cardBg:       '#213440',
    inputBg:      '#29404d',
    inputBorder:  '#345060',
    inputText:    '#bccdd9',
    overlay:      'rgba(21,34,41,0.7)',
    sidebarBg:    '#1b2b34',
    sidebarText:  '#bccdd9',
  },

  // Monokai Muted — warm charcoal, classic dev feel
  'dark-monokai': {
    name: 'Monokai',
    bg:           '#272822',
    bgSecondary:  '#2e2f2a',
    bgTertiary:   '#383930',
    surface:      '#2e2f2a',
    surfaceHover: '#383930',
    text:         '#d0cfbe',
    textSecondary:'#a2a18c',
    textMuted:    '#6a6a58',
    border:       '#49483e',
    borderLight:  '#3e3d32',
    navBg:        '#1e1f1a',
    navText:      '#eae9d8',
    footerBg:     '#1e1f1a',
    footerText:   '#a2a18c',
    cardBg:       '#2e2f2a',
    inputBg:      '#383930',
    inputBorder:  '#49483e',
    inputText:    '#d0cfbe',
    overlay:      'rgba(30,31,26,0.7)',
    sidebarBg:    '#272822',
    sidebarText:  '#d0cfbe',
  },

  // Nord — cool arctic blue, soft and soothing
  'dark-nord': {
    name: 'Nord',
    bg:           '#2e3440',
    bgSecondary:  '#3b4252',
    bgTertiary:   '#434c5e',
    surface:      '#3b4252',
    surfaceHover: '#434c5e',
    text:         '#d8dee9',
    textSecondary:'#a3b1c2',
    textMuted:    '#6b7d96',
    border:       '#4c566a',
    borderLight:  '#434c5e',
    navBg:        '#242933',
    navText:      '#ecf0f7',
    footerBg:     '#242933',
    footerText:   '#a3b1c2',
    cardBg:       '#3b4252',
    inputBg:      '#434c5e',
    inputBorder:  '#4c566a',
    inputText:    '#d8dee9',
    overlay:      'rgba(36,41,51,0.7)',
    sidebarBg:    '#2e3440',
    sidebarText:  '#d8dee9',
  },
}

// All dark theme keys for easy iteration
export const darkThemeKeys = ['dark', 'dark-dimmed', 'dark-oceanic', 'dark-monokai', 'dark-nord']

export const defaultTheme = 'light'
